import { resizeProperty } from '@/helpers';
import { ActionIcon, Box, Code, Text, UnstyledButton } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconExternalLink } from '@tabler/icons-react';
import KatexRenderer from '../RichTextEditor/KatexRenderer';
import { parseStyle } from '../RichTextEditor/utils';
import { WebsiteSimple } from '../Website/WebsiteSimple';
import { LazyImage } from '../RichTextEditor/ImageComponent';
import { Element } from './Element';

type Style = {
  color?: string;
  backgroundColor?: string;
};

export function TextFragment({ fragment, formValues, containerWidth }) {
  const type = fragment.type;
  const bold = Boolean(fragment.format & 1);
  const italic = Boolean(fragment.format & 2);
  const underline = Boolean(fragment.format & 8);
  const code = Boolean(fragment.format & 16);
  const strikethrough = Boolean(fragment.format & 4);
  const superscript = Boolean(fragment.format & 64);
  const subscript = Boolean(fragment.format & 32);
  const color = parseStyle(fragment.style, 'color');
  const backgroundColor = parseStyle(fragment.style, 'background-color');

  if (type === 'linebreak') {
    return <br />;
  }

  if (type === 'link') {
    return (
      <>
        <UnstyledButton
          pr="sm"
          component="span"
          c={formValues?.all_headings_font_color}
          fw={bold ? 700 : ''}
          fz={resizeProperty(containerWidth, formValues.body_font_size)}
          fs={italic ? 'italic' : ''}
          td={`underline ${strikethrough ? 'line-through' : ''}`.trim()}
          onClick={() =>
            modals.open({
              children: <WebsiteSimple src={fragment.url} closeModals={modals.closeAll} />,
              fullScreen: true,
              withCloseButton: false,
            })
          }
        >
          {fragment.children.map((childFragment) => (
            <TextFragment
              fragment={childFragment}
              formValues={formValues}
              containerWidth={containerWidth}
            />
          ))}
        </UnstyledButton>
        <ActionIcon
          variant="subtle"
          aria-label="Settings"
          component="a"
          href={fragment.url}
          target="_blank"
          color={formValues?.all_headings_font_color}
        >
          <IconExternalLink style={{ width: '100%', height: '100%' }} stroke={2} />
        </ActionIcon>
      </>
    );
  }

  if (code) {
    return (
      <Code
        fw={formValues.body_font_weight}
        fz={resizeProperty(containerWidth, formValues.body_font_size)}
        fs={formValues.body_italic ? 'italic' : 'normal'}
      >
        {fragment.text}
      </Code>
    );
  }

  if (type === 'equation') {
    return (
      <KatexRenderer
        equation={fragment.equation}
        inline={fragment.inline}
        onDoubleClick={() => {}}
        style={{
          fontSize: resizeProperty(containerWidth, formValues.body_font_size),
          color: formValues.body_font_color,
        }}
      />
    );
  }

  if (type === 'image') {
    return (
      <Box mb="md">
        <LazyImage
          src={fragment.src}
          alt={fragment.altText || 'Image'}
          mb={resizeProperty(containerWidth, 32)}
        />
        {fragment.caption.editorState?.root?.children?.map((child, index) => (
          <Element
            item={child}
            key={index}
            formValues={formValues}
            containerWidth={containerWidth}
          />
        ))}
      </Box>
    );
  }

  if (type === 'text') {
    let style: Style = {};
    if (color) style.color = color;
    if (backgroundColor) style.backgroundColor = backgroundColor;

    return (
      <Text
        fw={bold ? 700 : ''}
        fs={italic ? 'italic' : ''}
        td={`${underline ? 'underline' : ''} ${strikethrough ? 'line-through' : ''}`.trim()}
        style={style}
        span
        inherit
      >
        {superscript ? (
          <sup>{fragment.text}</sup>
        ) : subscript ? (
          <sub>{fragment.text}</sub>
        ) : (
          fragment.text
        )}
      </Text>
    );
  }
}
